import { Component, ElementRef, Input, QueryList, ViewChildren } from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormGroup,
    UntypedFormArray,
    UntypedFormControl,
    UntypedFormGroup
} from '@angular/forms';
import { TemplateBuilderService } from '@app/main/template-editor/services/template-builder.service';
import { ExplanationDisplayType } from '@shared/enums/explanation-display-type';
import { YesNoEnum } from '@shared/enums';
import { QuestionType } from '@shared/enums/QuestionType';
import { ITemplateAreaForm, ITemplateForm, ITemplateRowForm, ITemplateSectionForm } from '@shared/interfaces';

@Component({
    selector: 'app-template-editor-form',
    templateUrl: './template-editor-form.component.html',
    styleUrls: ['./template-editor-form.component.scss'],
    standalone: false
})
export class TemplateEditorFormComponent {
    @Input() builder: TemplateBuilderService;

    @ViewChildren('sectionTitle') sectionTitles: QueryList<ElementRef>;
    protected YesNoEnum = YesNoEnum;

    protected get formGroup(): FormGroup<ITemplateForm> {
        return this.builder.formGroup;
    }

    protected get sections(): FormArray<FormGroup<ITemplateSectionForm>> {
        return this.formGroup.controls.sections;
    }

    protected get sectionControls(): FormGroup<ITemplateSectionForm>[] {
        return this.sections.controls;
    }

    protected get questionType(): typeof QuestionType {
        return QuestionType;
    }

    protected sectionAreas(section: FormGroup<ITemplateSectionForm>): FormGroup<ITemplateAreaForm>[] {
        return section.controls.areas.controls;
    }

    protected areaRows(area: FormGroup<ITemplateAreaForm>): FormArray<FormGroup<ITemplateRowForm>>[] {
        return area.controls.rows.controls;
    }

    protected areaRowRows(control: UntypedFormArray): Array<UntypedFormGroup> {
        return control.controls as Array<UntypedFormGroup>;
    }

    protected isYesNoQuestion(control: FormGroup<ITemplateAreaForm> | UntypedFormArray): boolean {
        return control.value.questionType === QuestionType.yesNo;
    }

    protected get currentLang(): string {
        return this.builder.currentEditingLang;
    }

    protected area(item: AbstractControl): UntypedFormArray {
        return item as UntypedFormArray;
    }

    protected row(item: AbstractControl): UntypedFormArray {
        return item as UntypedFormArray;
    }

    protected setHeight(question: HTMLElement) {
        question.style.height = question.scrollHeight + 'px';
    }

    protected getControl(item: AbstractControl, name: string): UntypedFormControl {
        return item.get(name + (this.currentLang === 'en' ? '' : 'De')) as UntypedFormControl;
    }

    protected toggleGuidanceText(
        addGuidanceButton: HTMLElement,
        addGuidanceArea: HTMLElement,
        ar: AbstractControl
    ): void {
        addGuidanceButton.classList.toggle('hide');
        addGuidanceArea.classList.toggle('hide');
        ar.get('guidanceText' + (this.currentLang === 'en' ? '' : 'De')).patchValue('');
    }

    protected removeQuestion(sectionIndex: number, questionIndex: number): void {
        this.builder.removeQuestion(sectionIndex, questionIndex);
    }

    protected get activeSectionIndex(): number {
        return this.builder.activeSection;
    }

    protected removeSection(index: number) {
        this.builder.removeSection(index);
    }

    protected setItem(index: number) {
        this.builder.setActiveEditSection(index);
    }

    protected addSection() {
        this.builder.addSection();
    }

    protected addQuestion(sectionIndex: number, questionType: QuestionType) {
        this.builder.addQuestion(sectionIndex, questionType);
    }

    protected hasGuidanceValue(area: UntypedFormGroup): boolean {
        return !!area.get('guidanceText' + (this.currentLang === 'en' ? '' : 'De'))?.value;
    }

    protected get explanationDisplayType(): string[] {
        return Object.keys(ExplanationDisplayType);
    }

    protected getYesNoQuestions(section: FormGroup<ITemplateSectionForm>, areaIndex) {
        const areasWithIndex = this.sectionAreas(section).map((area, idx) => ({ area, idx }));
        const yesNoQuestions = areasWithIndex
            .filter(({ idx }) => idx < areaIndex)
            .filter(({ area }) => area.controls.questionType.value === QuestionType.yesNo);
        return yesNoQuestions;
    }
}
